/** Settings → Agency (C0): legal identity and contact details printed on every document. */

import {
  type AgencyProfile,
  type PatchedAgencyProfileRequest,
  getAgencyProfileCurrentRetrieveQueryKey,
  useAgencyProfileCurrentPartialUpdate,
  useAgencyProfileCurrentRetrieve,
} from "@agencyos/api-client";
import {
  type OpeningHours,
  WEEKDAYS,
  type Weekday,
  buildOpeningHours,
  formatRanges,
} from "@agencyos/core";
import { Button, GlassCard, Skeleton, SoftToggle, TextAreaField, TextField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { SettingsLayout } from "../components/SettingsLayout";
import { fieldErrors, useCanEditSettings } from "../lib/settings";

type TextKey =
  | "trade_name" | "slogan" | "legal_name" | "legal_form" | "rccm" | "nui" | "tax_center"
  | "phone_e164" | "whatsapp_e164" | "email" | "website" | "legal_rep_name" | "legal_rep_title"
  | "address" | "maps_url" | "payment_details" | "document_prefix"; // prettier-ignore

const GROUPS: { title: string; fields: TextKey[] }[] = [
  {
    title: "agency.identity",
    fields: ["trade_name", "slogan", "legal_name", "legal_form", "rccm", "nui", "tax_center", "legal_rep_name", "legal_rep_title"],
  },
  { title: "agency.contact", fields: ["phone_e164", "whatsapp_e164", "email", "website"] },
  { title: "agency.office", fields: ["address", "maps_url"] },
];
const METHODS = ["cash", "bank_transfer", "mtn_momo", "orange_money", "card"] as const;
const HINTS: Partial<Record<TextKey, string>> = {
  phone_e164: "agency.phoneHint",
  whatsapp_e164: "agency.phoneHint",
  document_prefix: "agency.prefixHint",
  payment_details: "agency.paymentDetailsHint",
};
const LONG: TextKey[] = ["address", "payment_details"];
const TEXT_KEYS: TextKey[] = [...GROUPS.flatMap((g) => g.fields), "payment_details", "document_prefix"];

function textValues(profile: AgencyProfile): Record<TextKey, string> {
  return Object.fromEntries(TEXT_KEYS.map((k) => [k, String(profile[k] ?? "")])) as Record<
    TextKey,
    string
  >;
}

function AgencyForm({ profile }: { profile: AgencyProfile }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const canEdit = useCanEditSettings();
  const save = useAgencyProfileCurrentPartialUpdate({
    mutation: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: getAgencyProfileCurrentRetrieveQueryKey() }),
    },
  });
  const [values, setValues] = useState<Record<TextKey, string>>(() => textValues(profile));
  const [methods, setMethods] = useState<string[]>(profile.payment_methods ?? []);
  const [hours, setHours] = useState<Partial<Record<Weekday, string>>>(() => {
    const stored = (profile.opening_hours ?? {}) as OpeningHours;
    return Object.fromEntries(WEEKDAYS.map((d) => [d, formatRanges(stored[d])]));
  });
  const [invalidDays, setInvalidDays] = useState<Weekday[]>([]);
  const errors = fieldErrors(save.error);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const opening = buildOpeningHours(hours);
    setInvalidDays(opening.invalid);
    if (opening.invalid.length) return;
    const data = {
      ...values,
      payment_methods: methods,
      opening_hours: opening.hours,
    } as PatchedAgencyProfileRequest;
    save.mutate(
      { data },
      {
        // Show what the API saved (e.g. 679… becomes +237679…).
        onSuccess: (response) => {
          if (response.status === 200) setValues(textValues(response.data));
        },
      },
    );
  };

  const field = (key: TextKey) => {
    // D-123: React keys are passed directly, never spread (console warning on every render)
    const common = {
      label: t(`agency.fields.${key}`),
      value: values[key],
      disabled: !canEdit,
      error: errors[key],
      hint: HINTS[key] ? t(HINTS[key]) : undefined,
    };
    const onChange = (value: string) => setValues((v) => ({ ...v, [key]: value }));
    return LONG.includes(key) ? (
      <TextAreaField key={key} {...common} rows={3} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <TextField
        key={key}
        {...common}
        type={key === "email" ? "email" : key.endsWith("e164") ? "tel" : "text"}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  };

  return (
    <form onSubmit={onSubmit} className="stack" noValidate>
      <div className="grid">
        {GROUPS.map((group) => (
          <GlassCard key={group.title} as="section" className="stack">
            <h2>{t(group.title)}</h2>
            {group.fields.map(field)}
          </GlassCard>
        ))}
        <GlassCard as="section" className="stack">
          <h2>{t("agency.hours")}</h2>
          <p className="muted">{t("agency.hoursHint")}</p>
          {WEEKDAYS.map((day) => (
            <TextField
              key={day}
              label={t(`agency.days.${day}`)}
              value={hours[day] ?? ""}
              placeholder="08:00-12:30, 14:00-17:30"
              disabled={!canEdit}
              error={invalidDays.includes(day) ? t("agency.hoursInvalid") : errors.opening_hours}
              onChange={(e) => setHours((h) => ({ ...h, [day]: e.target.value }))}
            />
          ))}
        </GlassCard>
        <GlassCard as="section" className="stack">
          <h2>{t("agency.payments")}</h2>
          <fieldset className="stack plain-fieldset" disabled={!canEdit}>
            <legend className="ao-field__label">{t("agency.paymentMethods")}</legend>
            {METHODS.map((method) => (
              <SoftToggle
                key={method}
                label={t(`agency.methods.${method}`)}
                checked={methods.includes(method)}
                disabled={!canEdit}
                onChange={(on) =>
                  setMethods((m) => (on ? [...m, method] : m.filter((x) => x !== method)))
                }
              />
            ))}
          </fieldset>
          {field("payment_details")}
          {field("document_prefix")}
        </GlassCard>
      </div>
      {canEdit ? (
        <div className="row sticky-actions">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? t("common.loading") : t("common.save")}
          </Button>
          <p role="status" className={save.isError ? "ko" : "ok"}>
            {save.isSuccess ? t("common.saved") : save.isError ? t("common.fixErrors") : ""}
          </p>
        </div>
      ) : null}
    </form>
  );
}

export function AgencyPage() {
  const { t } = useTranslation();
  const profile = useAgencyProfileCurrentRetrieve();
  return (
    <SettingsLayout title={t("agency.title")} intro={t("agency.intro")}>
      {profile.isPending ? (
        <GlassCard aria-busy="true">
          <Skeleton />
        </GlassCard>
      ) : profile.data?.status === 200 ? (
        <AgencyForm profile={profile.data.data} />
      ) : (
        <GlassCard role="alert">{t("common.error")}</GlassCard>
      )}
    </SettingsLayout>
  );
}
